/**
 * Design tokens for consistent styling across apps
 */

export const tokens = {
  /**
   * Typography
   */
  fonts: {
    sans: ['Heebo', 'Arial', 'sans-serif'],
    hebrew: ['Heebo', 'Noto Sans Hebrew', 'Arial', 'sans-serif'],
  },

  fontSizes: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    base: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },

  fontWeights: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900',
  },

  /**
   * Colors
   */
  colors: {
    brand: {
      primary: '#0C1A55',
      secondary: '#3C5FA8',
      accent: '#06b6d4',
    },
    status: {
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-400',
      toPay: 'bg-amber-100 text-amber-800 border-amber-400',
      inProgress: 'bg-sky-100 text-sky-800 border-sky-400',
    },
    /**
     * @deprecated Legacy festive palette — do not use in calm-v2 screens.
     * Kept only while existing references remain. See `v2/tokens.ts`.
     */
    unicorn: {
      purple: '#9333ea',
      pink: '#ec4899',
      blue: '#3b82f6',
      cyan: '#06b6d4',
    },
    // Pricing Workspace palette — used by the Pricing Matrix redesign.
    // All hex values are raw colors, not Tailwind classes (see src/features/pricing-workspace).
    pricing: {
      navy: '#0C1A55',
      blue: '#3C5FA8',
      green: '#16A34A',
      amber: '#D97706',
      red: '#DC2626',
      softBg: '#F1F4FB',
      text: '#1E293B',
      textSub: '#475569',
      textMute: '#94A3B8',
      line: '#E2E8F0',
      zebra: '#FAFBFC',
      // Status chip palette — bg/fg pairs used for source chips and badges
      chip: {
        supplier: { bg: '#DCFCE7', fg: '#166534' },
        manual: { bg: '#F3E8FF', fg: '#6B21A8' },
        known: { bg: '#DBEAFE', fg: '#1E40AF' },
        warning: { bg: '#FEF3C7', fg: '#92400E' },
        danger: { bg: '#FEE2E2', fg: '#991B1B' },
        info: { bg: '#DBEAFE', fg: '#1E40AF' },
        success: { bg: '#D1FAE5', fg: '#065F46' },
        default: { bg: '#F1F5F9', fg: '#475569' },
      },
      // Supplier-status dot colors (pending / partial-response / fully-responded)
      dot: {
        pending: { bg: '#F1F5F9', fg: '#64748B', dot: '#94A3B8' },
        partial: { bg: '#FEF3C7', fg: '#92400E', dot: '#D97706' },
        responded: { bg: '#F0FDF4', fg: '#15803D', dot: '#16A34A' },
      },
    },
  },

  /**
   * Reusable visual recipes for the Pricing Workspace redesign.
   * These are CSS-in-JS style objects (inline-style compatible) so they can be consumed
   * directly without a CSS framework dependency. Prefer Tailwind classes for new work.
   * @deprecated for elka-manager/crm calm-v2 screens — gradients/glass are out of the
   * calm visual language. Still valid inside apps/quotes (its design system is separate).
   */
  recipes: {
    glassCard: {
      background: 'rgba(255,255,255,0.90)',
      border: '1px solid rgba(255,255,255,0.96)',
      borderRadius: 14,
      boxShadow: '0 1px 3px rgba(12,26,85,0.06), 0 8px 24px -8px rgba(12,26,85,0.08)',
    },
    primaryButtonGradient: 'linear-gradient(to right, #0C1A55, #3C5FA8, #0C1A55)',
    headerGradient: 'linear-gradient(to right, #0C1A55, #3C5FA8, #0C1A55)',
    chapterHeaderGradient: 'linear-gradient(to left, #EEF4FF, #F8FAFC)',
    softBgRadials:
      'radial-gradient(ellipse 90% 60% at 5% 0%, rgba(60,95,168,0.11) 0%, transparent 55%),' +
      'radial-gradient(ellipse 70% 50% at 95% 100%, rgba(12,26,85,0.08) 0%, transparent 55%)',
  },

  /**
   * Spacing scale (in rem)
   */
  spacing: {
    xs: '0.25rem', // 4px
    sm: '0.5rem', // 8px
    md: '1rem', // 16px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
    '2xl': '3rem', // 48px
  },

  /**
   * Border radius
   */
  radius: {
    sm: '0.25rem', // 4px
    md: '0.5rem', // 8px
    lg: '0.75rem', // 12px
    xl: '1rem', // 16px
    full: '9999px',
  },

  /**
   * Shadows
   */
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  },

  /**
   * Breakpoints (in pixels)
   */
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

export type Tokens = typeof tokens;
